// These mixins are plain CSS objects with no theme callbacks, so they are left
// deliberately theme-agnostic (`as const`) rather than typed against a specific
// Theme: the styles files that consume them variously import Theme from
// @mui/system, @mui/material/styles and @emotion/react, and those are three
// mutually incompatible types.
//
// That is also why the brand tokens below are `var(--ch-*)` strings rather than
// hex. A clinic's colours are only known once the session says which clinic it
// is, and re-deriving these objects per tenant would mean a callback -- exactly
// the thing that cannot be typed here. Pointing at custom properties instead
// lets the browser do the substitution: BrandingContext sets the variables and
// every one of these repaints, with no React render and no new types.
//
// The variables always resolve: index.css declares Carefull's values on :root, so
// anything drawn before a clinic is known -- or by a clinic that chose nothing --
// gets the platform palette rather than an empty string.
//
// Literal hex is still correct for anything that is *not* the brand. Ink, the
// warm surface, and the semantic colours in the theme do not change per clinic,
// and making them configurable would multiply the ways a clinic can render its
// own app unreadable.

// ---------------------------------------------------------------------------
// Brand tokens — resolved at runtime from the tenant's branding
// ---------------------------------------------------------------------------
export const brandColors = {
  primary: 'var(--ch-primary)',
  primaryDark: 'var(--ch-primary-dark)',
  primaryLight: 'var(--ch-primary-light)',
  /** Legible text on a filled primary surface — computed, not assumed white. */
  primaryContrast: 'var(--ch-primary-contrast)',
  secondary: 'var(--ch-secondary)',
  secondaryDark: 'var(--ch-secondary-dark)',
  secondaryLight: 'var(--ch-secondary-light)',
  secondaryContrast: 'var(--ch-secondary-contrast)',

  // Not per-clinic, and deliberately so.
  foreground: '#1A1A2E', // near-navy text
  cream: '#FDFBF6', // warm off-white section surface
} as const;

/**
 * A brand colour at partial opacity.
 *
 * `rgba()` cannot take a variable for just the hue, so the tokens also publish an
 * `--ch-*-rgb` triple ("0, 72, 104") and this wraps it. Every tinted hover, pill
 * and icon tile in the app goes through here rather than hardcoding a channel
 * set that would stay blue on a green clinic.
 */
export const brandAlpha = (
  colour: 'primary' | 'secondary',
  opacity: number,
): string => `rgba(var(--ch-${colour}-rgb), ${opacity})`;

// Signature gradients, rebuilt on the tokens
export const gradients = {
  // Soft hero wash: brand -> warm surface -> accent
  heroWash: `linear-gradient(135deg, ${brandAlpha('primary', 0.06)} 0%, ${brandColors.cream} 55%, ${brandAlpha('secondary', 0.12)} 100%)`,
  // Full-bleed CTA band
  brandCta: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.primaryLight} 100%)`,
  // Thin primary -> accent bar (card tops, dividers)
  brandBar: `linear-gradient(90deg, ${brandColors.primary} 0%, ${brandColors.secondaryLight} 50%, ${brandColors.secondary} 100%)`,
} as const;

// ---------------------------------------------------------------------------
// Reusable sx mixins — replace the copy-pasted inline hover/pill recipes
// ---------------------------------------------------------------------------

/**
 * A hover rule that only exists for pointers that can actually hover.
 *
 * A touch browser has no hover, so it fakes one: the last-tapped element keeps
 * `:hover` until something else is tapped. Unguarded, that leaves a card lifted
 * 4px with a 32px shadow, or a button shifted up 1px, sitting there long after
 * the tap -- reading as "still selected" when nothing is.
 *
 * `(hover: hover)` asks the primary input whether hovering is possible at all,
 * which is the actual question. Width is the wrong proxy: a touchscreen laptop
 * is wide and cannot hover, and a mouse plugged into a phone can.
 *
 * The tap still gets feedback -- MUI's ripple is a press state, not a hover one,
 * and is unaffected by this.
 *
 * Spread it where a bare `'&:hover'` key would have gone:
 *
 *   ...hoverOnly({ transform: 'translateY(-4px)' }),
 */
export const hoverOnly = <T extends object>(rules: T): { '@media (hover: hover)': { '&:hover': T } } => ({
  '@media (hover: hover)': { '&:hover': rules },
});

/**
 * Rules that apply only where the pointer is a finger.
 *
 * The counterpart to `hoverOnly`, and it asks a different question on purpose. Hover is
 * about whether a hover state can exist at all; this is about precision -- a fingertip
 * covers roughly 44px and a mouse cursor covers one, so a control sized for the cursor is
 * a control a finger keeps missing.
 *
 * Scoped rather than applied everywhere because a 44px minimum is right for a thumb and
 * wrong for a dense desktop control: growing every small button on a mouse-driven screen
 * would push apart layouts that read perfectly well as they are.
 *
 * Unlike `hoverOnly` these are plain rules, not a pseudo-class block:
 *
 *   ...coarsePointerOnly({ minHeight: 44 }),
 */
export const coarsePointerOnly = <T extends object>(rules: T): { '@media (pointer: coarse)': T } => ({
  '@media (pointer: coarse)': rules,
});

// Gentle springy hover-lift for cards/tiles
export const hoverLift = {
  transition: 'transform .3s ease, box-shadow .3s ease, border-color .3s ease',
  ...hoverOnly({
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(16,24,40,0.10)',
  }),
} as const;

// Interactive card: transparent border -> soft brand border + lift on hover
export const cardInteractive = {
  border: '2px solid transparent',
  transition: 'transform .3s ease, box-shadow .3s ease, border-color .3s ease',
  ...hoverOnly({
    transform: 'translateY(-4px)',
    boxShadow: '0 16px 32px rgba(16,24,40,0.10)',
    borderColor: brandAlpha('primary', 0.25),
  }),
} as const;

/**
 * A minimum height in *dynamic* viewport units, with a static fallback.
 *
 * `100vh` on a phone is the viewport with the browser chrome retracted, which is taller
 * than what is on screen when the chrome is showing. An element sized to it therefore
 * hangs below the fold on load and pulls a scrollbar onto a page that has nothing to
 * scroll. `dvh` tracks the viewport that actually exists at any moment.
 *
 * The fallback is derived from the argument rather than passed alongside it, so the two
 * cannot drift apart -- there is one number to change, and the `vh` version of it is
 * always the same expression.
 *
 *   ...minHeightDvh('100dvh')
 *   ...minHeightDvh('calc(100dvh - 200px)')
 */
export const minHeightDvh = (expression: string) => ({
  minHeight: expression.replace(/dvh/g, 'vh'),
  '@supports (min-height: 1dvh)': { minHeight: expression },
});

/**
 * For a `<form>` sitting between a Dialog's paper and its content.
 *
 * MUI builds a dialog as a flex column and relies on `DialogContent` being a direct child
 * that can grow, shrink and scroll while `DialogActions` stays put. A form wrapped around
 * both breaks that chain: it is a plain block, so it sizes to its content, cannot shrink,
 * and the whole paper -- title, fields and buttons together -- becomes the thing that
 * scrolls. On a 360px-tall landscape phone that put the submit button below the fold of a
 * dialog that had no scrollbar of its own.
 *
 * `minHeight: 0` is the half that is easy to miss: a flex item's default `min-height: auto`
 * refuses to shrink below its content, which is exactly what has to happen here.
 *
 * Applied via `<Box component="form">`, since a bare `<form>` takes no `sx`.
 */
export const dialogFormColumn = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  flex: '1 1 auto',
} as const;

// Small uppercase "eyebrow" pill placed above section headings
export const eyebrowPill = {
  display: 'inline-flex',
  alignItems: 'center',
  px: 1.75,
  py: 0.5,
  mb: 2,
  borderRadius: 999,
  backgroundColor: brandAlpha('primary', 0.1),
  color: brandColors.primary,
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.09em',
  textTransform: 'uppercase',
} as const;

// Rounded icon tile (soft tinted square) that holds a feature icon
export const iconTilePrimary = {
  width: 64,
  height: 64,
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: brandAlpha('primary', 0.1),
  color: brandColors.primary,
} as const;

export const iconTileSecondary = {
  width: 64,
  height: 64,
  borderRadius: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: brandAlpha('secondary', 0.16),
  color: brandColors.secondaryDark,
} as const;

// Thin primary->accent bar for the top edge of a card
export const accentBar = {
  height: 6,
  width: '100%',
  background: gradients.brandBar,
} as const;

// Lets a section escape the Layout's centred <Container maxWidth="lg"> and run
// edge-to-edge, while keeping its inner content aligned to the same gutter.
export const fullBleed = {
  width: '100vw',
  position: 'relative',
  left: '50%',
  right: '50%',
  ml: '-50vw',
  mr: '-50vw',
} as const;
